import { useState, useRef, useEffect, useCallback } from 'react';
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from '@aws-sdk/client-transcribe-streaming';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import { fetchAuthSession } from 'aws-amplify/auth';

interface TranscriptSegment {
  speaker_label?: string;
  content: string;
  timestamp: string;
  start_time?: number;
  end_time?: number;
  is_partial?: boolean;
}

interface TranscriptionSession {
  session_id: string;
  start_time: string;
  segments: TranscriptSegment[];
}

interface UseTranscribeMedicalConsultationProps {
  selectedLanguage?: string;
  sessionId: string;
}

const DEFAULT_SAMPLE_RATE = 16000;
const STORAGE_KEY_PREFIX = 'consultation_transcription_';
const SUPPORTED_LANGUAGE_CODES = ['en-US', 'ar-SA'];
// AWS Transcribe best practices: chunk size between 50-200ms
// At 16kHz, 100ms = 0.1 * 16000 * 2 = 3200 bytes
const OPTIMAL_CHUNK_SIZE = 3200; // 100ms of audio at 16kHz
const SCRIPT_PROCESSOR_BUFFER_SIZE = 2048; // Closest power of 2 for ~100ms

const useTranscribeMedicalConsultation = ({
  selectedLanguage = 'Auto-detect',
  sessionId
}: UseTranscribeMedicalConsultationProps) => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [transcribedSegments, setTranscribedSegments] = useState<TranscriptSegment[]>([]);
  const [isWaitingForFinalTranscript, setIsWaitingForFinalTranscript] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<Uint8Array[]>([]);
  const isProcessingRef = useRef(false);
  const transcribeClientRef = useRef<TranscribeStreamingClient | null>(null);
  const commandRef = useRef<any>(null);
  const storageKeyRef = useRef<string>(`${STORAGE_KEY_PREFIX}${sessionId}`);
  const waitingForFinalRef = useRef(false);
  const finalTranscriptTimerRef = useRef<NodeJS.Timeout | null>(null);
  const credentialExpirationRef = useRef<Date | null>(null);
  const credentialRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 3;

  // Initialize transcription session in local storage
  useEffect(() => {
    if (sessionId) {
      const existingData = localStorage.getItem(storageKeyRef.current);
      if (!existingData) {
        const session: TranscriptionSession = {
          session_id: sessionId,
          start_time: new Date().toISOString(),
          segments: []
        };
        localStorage.setItem(storageKeyRef.current, JSON.stringify(session));
      } else {
        // Load existing segments
        try {
          const session = JSON.parse(existingData) as TranscriptionSession;
          setTranscribedSegments(session.segments || []);
        } catch (error) {
          console.error('Error loading existing transcription data:', error);
        }
      }
    }
  }, [sessionId]);

  // Save segments to local storage whenever they change
  const saveToLocalStorage = useCallback((segments: TranscriptSegment[]) => {
    try {
      const session: TranscriptionSession = {
        session_id: sessionId,
        start_time: new Date().toISOString(),
        segments
      };
      localStorage.setItem(storageKeyRef.current, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving transcription to local storage:', error);
    }
  }, [sessionId]);

  const initTranscribeClient = async (forceRefresh: boolean = false): Promise<TranscribeStreamingClient> => {
    try {
      // Check if we can reuse existing client (credentials still valid for at least 5 minutes)
      if (!forceRefresh && transcribeClientRef.current && credentialExpirationRef.current) {
        const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
        if (credentialExpirationRef.current > fiveMinutesFromNow) {
          console.log('[AWS Transcribe] Reusing existing client, credentials still valid');
          return transcribeClientRef.current;
        }
      }

      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken?.toString()) {
        throw new Error('No valid ID token found.');
      }

      const regionFromEnv = import.meta.env.VITE_AWS_REGION;
      const userPoolIdFromEnv = import.meta.env.VITE_COGNITO_USER_POOL_ID;
      const identityPoolIdFromEnv = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;

      if (!regionFromEnv || !userPoolIdFromEnv || !identityPoolIdFromEnv) {
        throw new Error("AWS environment variables are not configured.");
      }

      const cognitoIdentityClient = new CognitoIdentityClient({ region: regionFromEnv });
      const issuerUrl = `cognito-idp.${regionFromEnv}.amazonaws.com/${userPoolIdFromEnv}`;

      const { IdentityId } = await cognitoIdentityClient.send(
        new GetIdCommand({
          IdentityPoolId: identityPoolIdFromEnv,
          Logins: { [issuerUrl]: tokens.idToken.toString() }
        })
      );

      if (!IdentityId) throw new Error('Could not get Identity ID.');

      const { Credentials } = await cognitoIdentityClient.send(
        new GetCredentialsForIdentityCommand({
          IdentityId,
          Logins: { [issuerUrl]: tokens.idToken.toString() }
        })
      );

      if (!Credentials?.AccessKeyId || !Credentials?.SecretKey || !Credentials?.SessionToken) {
        throw new Error('Could not retrieve temporary credentials.');
      }

      // Track credential expiration
      if (Credentials.Expiration) {
        credentialExpirationRef.current = Credentials.Expiration;
        console.log('[AWS Transcribe] Credentials will expire at:', Credentials.Expiration);
      }

      const client = new TranscribeStreamingClient({
        region: regionFromEnv,
        credentials: {
          accessKeyId: Credentials.AccessKeyId,
          secretAccessKey: Credentials.SecretKey,
          sessionToken: Credentials.SessionToken,
          expiration: Credentials.Expiration,
        },
      });

      return client;
    } catch (error) {
      console.error('Error initializing Transcribe client:', error);
      throw error;
    }
  };

  // Setup credential refresh interval when transcribing
  useEffect(() => {
    if (isTranscribing && !credentialRefreshIntervalRef.current) {
      // Check credentials every 5 minutes
      credentialRefreshIntervalRef.current = setInterval(async () => {
        if (credentialExpirationRef.current) {
          const tenMinutesFromNow = new Date(Date.now() + 10 * 60 * 1000);
          if (credentialExpirationRef.current < tenMinutesFromNow) {
            console.log('[AWS Transcribe] Credentials expiring soon, refreshing...');
            try {
              transcribeClientRef.current = await initTranscribeClient(true);
              console.log('[AWS Transcribe] Credentials refreshed successfully');
            } catch (err) {
              console.error('[AWS Transcribe] Failed to refresh credentials:', err);
            }
          }
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
    }

    return () => {
      if (credentialRefreshIntervalRef.current) {
        clearInterval(credentialRefreshIntervalRef.current);
        credentialRefreshIntervalRef.current = null;
      }
    };
  }, [isTranscribing]);

  const processTranscriptionResult = useCallback((result: any) => {
    const alternatives = result.Alternatives?.[0];
    if (!alternatives) return;

    const transcript = alternatives.Transcript || '';
    const items = alternatives.Items || [];

    // Log detected language if available
    if (result.LanguageCode) {
      console.log('[AWS Transcribe] Detected language:', result.LanguageCode);
    }

    if (result.IsPartial) {
      // Update current partial transcript
      setCurrentTranscript(transcript);
      console.log('[AWS Transcribe] Partial transcript:', transcript);

      // Reset final transcript timer
      if (finalTranscriptTimerRef.current) {
        clearTimeout(finalTranscriptTimerRef.current);
      }

      if (waitingForFinalRef.current) {
        // When waiting for final, set a timer to detect when no more transcripts are coming
        finalTranscriptTimerRef.current = setTimeout(() => {
          console.log('No more transcripts received, considering transcription complete');
          waitingForFinalRef.current = false;
          setIsWaitingForFinalTranscript(false);
        }, 3000); // Wait 3 seconds for final transcripts
      }
    } else {
      // Process final transcript with speaker diarization
      if (transcript.trim()) {
        console.log('[AWS Transcribe] Final transcript:', transcript);
        console.log('[AWS Transcribe] Items with speaker info:', items.map((item: any) => ({
          Speaker: item.Speaker,
          Content: item.Content,
          Type: item.Type
        })));

        const segments: TranscriptSegment[] = [];
        let currentSegment: TranscriptSegment | null = null;
        let currentSpeaker: string | undefined = undefined;
        let lastKnownSpeaker: string = '0'; // Default speaker when none identified
        let contentBuffer: string[] = [];

        // Process items and group by speaker
        items.forEach((item: any, index: number) => {
          // Skip punctuation-only items for speaker grouping
          if (item.Type === 'punctuation') {
            if (contentBuffer.length > 0) {
              contentBuffer[contentBuffer.length - 1] += item.Content || '';
            }
            return;
          }

          const speaker = item.Speaker;
          const content = item.Content || '';

          // Update last known speaker if we have one
          if (speaker !== undefined && speaker !== null) {
            lastKnownSpeaker = speaker;
          }

          // Use the speaker or fall back to last known speaker
          const effectiveSpeaker = speaker !== undefined ? speaker : lastKnownSpeaker;

          if (effectiveSpeaker !== currentSpeaker) {
            // Speaker change detected - save current segment
            if (currentSegment && contentBuffer.length > 0) {
              currentSegment.content = contentBuffer.join(' ').trim();
              if (currentSegment.content) {
                segments.push(currentSegment);
              }
            }

            // Start new segment
            contentBuffer = [content];
            currentSegment = {
              speaker_label: `Speaker_${effectiveSpeaker}`,
              content: '', // Will be set when segment is complete
              timestamp: new Date().toISOString(),
              start_time: item.StartTime,
              end_time: item.EndTime,
              is_partial: false
            };
            currentSpeaker = effectiveSpeaker;
          } else {
            // Same speaker - accumulate content
            contentBuffer.push(content);
            if (currentSegment) {
              currentSegment.end_time = item.EndTime;
            }
          }
        });

        // Add the last segment
        if (currentSegment && contentBuffer.length > 0) {
          currentSegment.content = contentBuffer.join(' ').trim();
          if (currentSegment.content) {
            segments.push(currentSegment);
          }
        }

        // Fallback: If no segments were created but we have transcript, create one with default speaker
        if (segments.length === 0 && transcript.trim()) {
          segments.push({
            speaker_label: 'Speaker_0', // Default speaker label
            content: transcript,
            timestamp: new Date().toISOString(),
            start_time: items[0]?.StartTime || 0,
            end_time: items[items.length - 1]?.EndTime || 0,
            is_partial: false
          });
        }

        console.log('[AWS Transcribe] Processed segments:', segments);

        // Update transcribed segments and save to local storage
        setTranscribedSegments(prev => {
          const newSegments = [...prev, ...segments];
          saveToLocalStorage(newSegments);
          console.log('[AWS Transcribe] Total segments saved:', newSegments.length);
          return newSegments;
        });

        setCurrentTranscript('');
      }
    }
  }, [saveToLocalStorage]);

  const startTranscription = useCallback(async () => {
    if (isProcessingRef.current) {
      console.log('🔄 Transcription already in progress');
      return;
    }

    setCurrentTranscript('');
    isProcessingRef.current = true;

    try {
      console.log('🎤 Starting transcription with diarization...');

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: DEFAULT_SAMPLE_RATE
        }
      });
      streamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: DEFAULT_SAMPLE_RATE });
      audioContextRef.current = audioContext;

      const sourceNode = audioContext.createMediaStreamSource(stream);
      const processorNode = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
      processorNodeRef.current = processorNode;

      processorNode.onaudioprocess = (event) => {
        if (!isProcessingRef.current) return;

        // Check if microphone is muted
        const isMuted = streamRef.current ?
          !streamRef.current.getAudioTracks()[0]?.enabled : false;

        if (isMuted) {
          // Send silence when muted to keep stream alive
          const silenceData = new Int16Array(event.inputBuffer.length).fill(0);
          audioQueueRef.current.push(new Uint8Array(silenceData.buffer));
        } else {
          // Process actual audio
          const inputData = event.inputBuffer.getChannelData(0);
          const pcmData = new Int16Array(inputData.length);

          for (let i = 0; i < inputData.length; i++) {
            const sample = Math.max(-1, Math.min(1, inputData[i]));
            pcmData[i] = sample * 0x7FFF;
          }

          audioQueueRef.current.push(new Uint8Array(pcmData.buffer));
        }
      };

      sourceNode.connect(processorNode);
      processorNode.connect(audioContext.destination);

      if (!transcribeClientRef.current) {
        transcribeClientRef.current = await initTranscribeClient();
      }

      const audioStream = async function*() {
        while (isProcessingRef.current || audioQueueRef.current.length > 0) {
          if (audioQueueRef.current.length > 0) {
            const chunk = audioQueueRef.current.shift();
            if (chunk) {
              yield { AudioEvent: { AudioChunk: chunk } };
            }
          } else {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }
      };

      setIsTranscribing(true);

      let commandParams: any = {
        MediaEncoding: 'pcm',
        MediaSampleRateHertz: DEFAULT_SAMPLE_RATE,
        AudioStream: audioStream(),
        ShowSpeakerLabel: true,
      };

      if (selectedLanguage === 'Auto-detect') {
        commandParams = {
          ...commandParams,
          IdentifyLanguage: true,
          LanguageOptions: SUPPORTED_LANGUAGE_CODES.join(','),
          PreferredLanguages: SUPPORTED_LANGUAGE_CODES,
        };
        console.log('[AWS Transcribe] Auto-detect enabled with languages:', SUPPORTED_LANGUAGE_CODES.join(', '));
      } else {
        const specificLanguageCode = selectedLanguage === 'English' ? 'en-US' : (selectedLanguage === 'Arabic' ? 'ar-SA' : 'en-US');
        commandParams = {
          ...commandParams,
          LanguageCode: specificLanguageCode,
        };
        console.log('[AWS Transcribe] Using specific language:', specificLanguageCode);
      }

      const command = new StartStreamTranscriptionCommand(commandParams);
      commandRef.current = command;

      const data = await transcribeClientRef.current.send(command);

      if (data.TranscriptResultStream) {
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;

        for await (const event of data.TranscriptResultStream) {
          if (!isProcessingRef.current && !waitingForFinalRef.current) break;

          if (event.TranscriptEvent?.Transcript?.Results) {
            event.TranscriptEvent.Transcript.Results.forEach(processTranscriptionResult);
          }
        }
      }
    } catch (error: any) {
      console.error('❌ Error in transcription:', error);

      // Check if this is a recoverable network error
      const isNetworkError = error.name === 'NetworkError' ||
        error.message?.includes('network') ||
        error.message?.includes('connection') ||
        error.code === 'ECONNRESET';

      if (isNetworkError && reconnectAttemptsRef.current < maxReconnectAttempts && isProcessingRef.current) {
        reconnectAttemptsRef.current++;
        console.log(`[AWS Transcribe] Network error, attempting reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);

        // Wait before reconnecting (exponential backoff)
        const backoffMs = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, backoffMs));

        // Refresh credentials and try to restart
        try {
          transcribeClientRef.current = await initTranscribeClient(true);
          // The audio stream and context are still active, restart transcription
          console.log('[AWS Transcribe] Reconnecting transcription stream...');
          // Recursive call to restart the transcription with existing audio
          return startTranscription();
        } catch (reconnectError) {
          console.error('[AWS Transcribe] Reconnection failed:', reconnectError);
        }
      }

      stopTranscription();
      throw error;
    }
  }, [processTranscriptionResult, selectedLanguage]);

  const pauseTranscription = useCallback(async () => {
    console.log('⏸️ Pausing transcription...');

    // Mute the microphone - keep stream and transcribe connection alive
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => track.enabled = false);
    }

    setIsPaused(true);
    // Note: isTranscribing stays true because the stream is still active (just muted)
  }, []);

  const resumeTranscription = useCallback(async () => {
    console.log('▶️ Resuming transcription...');

    // Resume AudioContext if it was suspended
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('[AWS Transcribe] AudioContext resumed');
      } catch (err) {
        console.error('[AWS Transcribe] Failed to resume AudioContext:', err);
      }
    }

    // Unmute the microphone
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => track.enabled = true);
    }

    setIsPaused(false);
  }, []);

  const stopTranscription = useCallback(() => {
    console.log('🛑 Stopping transcription...');

    if (finalTranscriptTimerRef.current) {
      clearTimeout(finalTranscriptTimerRef.current);
      finalTranscriptTimerRef.current = null;
    }

    // Clear credential refresh interval
    if (credentialRefreshIntervalRef.current) {
      clearInterval(credentialRefreshIntervalRef.current);
      credentialRefreshIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (processorNodeRef.current && audioContextRef.current) {
      processorNodeRef.current.disconnect();
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(e => console.error("Error closing AudioContext:", e));
      }
      processorNodeRef.current = null;
      audioContextRef.current = null;
    }

    isProcessingRef.current = false;
    audioQueueRef.current = [];
    setIsTranscribing(false);
    setIsPaused(false);
    setIsWaitingForFinalTranscript(false);
    waitingForFinalRef.current = false;

    console.log('🔴 Medical transcription stopped and resources cleaned up.');
  }, []);

  const waitForFinalTranscripts = useCallback(async (): Promise<void> => {
    console.log('⏳ Waiting for final transcripts...');

    waitingForFinalRef.current = true;
    setIsWaitingForFinalTranscript(true);

    // Stop sending new audio but keep connection open for final transcripts
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => track.enabled = false);
    }

    // Wait for final transcripts with timeout
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.log('✅ Final transcript wait timeout reached');
        waitingForFinalRef.current = false;
        setIsWaitingForFinalTranscript(false);
        resolve();
      }, 5000); // Maximum 5 seconds wait for final transcripts

      // Check periodically if we're done waiting
      const checkInterval = setInterval(() => {
        if (!waitingForFinalRef.current) {
          clearTimeout(timeout);
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }, []);

  const getFullTranscript = useCallback((): string => {
    return transcribedSegments
      .map(segment => {
        const speaker = segment.speaker_label ? `[${segment.speaker_label}]: ` : '';
        return `${speaker}${segment.content}`;
      })
      .join('\n');
  }, [transcribedSegments]);

  const getTranscriptionData = useCallback((): TranscriptionSession => {
    return {
      session_id: sessionId,
      start_time: new Date().toISOString(),
      segments: transcribedSegments
    };
  }, [sessionId, transcribedSegments]);

  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem(storageKeyRef.current);
    setTranscribedSegments([]);
  }, []);

  // Handle visibility change - resume AudioContext when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isProcessingRef.current) {
        // Resume AudioContext if it was suspended (browsers suspend on tab switch)
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          try {
            await audioContextRef.current.resume();
            console.log('[AWS Transcribe] AudioContext resumed after visibility change');
          } catch (err) {
            console.error('[AWS Transcribe] Failed to resume AudioContext:', err);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("Component unmounting, initiating cleanup.");
      stopTranscription();
    };
  }, [stopTranscription]);

  return {
    isTranscribing,
    isPaused,
    currentTranscript,
    transcribedSegments,
    isWaitingForFinalTranscript,
    startTranscription,
    pauseTranscription,
    resumeTranscription,
    stopTranscription,
    waitForFinalTranscripts,
    getFullTranscript,
    getTranscriptionData,
    clearLocalStorage
  };
};

export default useTranscribeMedicalConsultation;